import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { MINERAL_COLORS } from "./MineralLayout";
import {
  Package, Plus, RefreshCw, CheckCircle2, XCircle, AlertCircle, Loader2, Save, DollarSign,
} from "lucide-react";

function StatusPill({ s }) {
  const cfg = {
    synced:  { Icon: CheckCircle2, cls: "text-emerald-700 bg-emerald-50 border-emerald-200", label: "Sincronizado" },
    pending: { Icon: AlertCircle,  cls: "text-slate-600 bg-slate-50 border-slate-200",        label: "Pendente" },
    syncing: { Icon: Loader2,      cls: "text-amber-700 bg-amber-50 border-amber-200",        label: "Sincronizando" },
    partial: { Icon: AlertCircle,  cls: "text-amber-700 bg-amber-50 border-amber-200",        label: "Parcial" },
    failed:  { Icon: XCircle,      cls: "text-rose-700 bg-rose-50 border-rose-200",           label: "Falhou" },
  }[s] || { Icon: AlertCircle, cls: "text-slate-500 bg-slate-50 border-slate-200", label: s };
  const { Icon } = cfg;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-[11px] font-medium ${cfg.cls}`}>
      <Icon className={`w-3 h-3 ${s === "syncing" ? "animate-spin" : ""}`}/> {cfg.label}
    </span>
  );
}

export default function MineralAdminCommerce() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [editing, setEditing] = useState(null); // code do produto sendo editado

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/mineral/admin/commerce/products");
      setItems(r.data.items || []);
    } catch { toast.error("Falha ao carregar produtos"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const seed = async () => {
    setBusy(true);
    try {
      const r = await api.post("/mineral/admin/commerce/products/seed-defaults");
      const created = r.data.created || [];
      const skipped = r.data.skipped || [];
      toast.success(`Seed: ${created.length} criados, ${skipped.length} já existiam`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Seed falhou"); }
    finally { setBusy(false); }
  };

  const retry = async (code) => {
    setBusy(true);
    try {
      await api.post(`/mineral/admin/commerce/products/${code}/retry-sync`);
      toast.success(`${code} sincronizado`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync falhou"); }
    finally { setBusy(false); }
  };

  const save = async (code, patch) => {
    setBusy(true);
    try {
      await api.patch(`/mineral/admin/commerce/products/${code}`, patch);
      toast.success(`${code} atualizado`);
      setEditing(null);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Update falhou"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="mineral-admin-commerce">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-widest" style={{ color: MINERAL_COLORS.goldDark }}>
            Administração
          </div>
          <h2 className="font-heading text-2xl font-semibold" style={{ color: MINERAL_COLORS.navy }}>
            <Package className="inline w-6 h-6 mr-2" style={{ color: MINERAL_COLORS.gold }}/>
            Produtos Comerciais
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Preços não são hardcoded no frontend. Toda alteração dispara novo Stripe Price e preserva histórico.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} disabled={busy}
            data-testid="mineral-admin-commerce-refresh"
            className="px-3 py-2 rounded-lg border border-slate-300 text-sm hover:bg-slate-50 inline-flex items-center gap-1.5">
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? "animate-spin" : ""}`}/> Atualizar
          </button>
          {items.length === 0 && (
            <button onClick={seed} disabled={busy}
              data-testid="mineral-admin-commerce-seed"
              className="px-4 py-2 rounded-lg text-sm font-medium text-white inline-flex items-center gap-2"
              style={{ background: MINERAL_COLORS.gold }}>
              {busy ? <Loader2 className="w-4 h-4 animate-spin"/> : <Plus className="w-4 h-4"/>}
              Criar 4 produtos padrão
            </button>
          )}
        </div>
      </div>

      <div className="bg-white border rounded-2xl overflow-hidden" style={{ borderColor: MINERAL_COLORS.divider }}>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-500">Carregando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center">
            <Package className="w-10 h-10 mx-auto text-slate-300 mb-3"/>
            <div className="text-sm text-slate-500 mb-2">Nenhum produto cadastrado ainda.</div>
            <div className="text-xs text-slate-400">Clique em "Criar 4 produtos padrão" para começar com os defaults da cláusula 106.</div>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Código</th>
                <th className="text-left px-4 py-3">Nome</th>
                <th className="text-right px-4 py-3">Preço</th>
                <th className="text-left px-4 py-3">Stripe</th>
                <th className="text-left px-4 py-3">Product ID</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map(p => (
                <ProductRow key={p.code} p={p} editing={editing === p.code}
                              onEdit={() => setEditing(p.code)}
                              onCancel={() => setEditing(null)}
                              onSave={(patch) => save(p.code, patch)}
                              onRetry={() => retry(p.code)}
                              busy={busy}/>
              ))}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-[11px] text-slate-400">
        Regra Hermes: sincronização com Stripe é obrigatória antes de publicar. Se falhar, o botão "Tentar Novamente" fica disponível.
      </p>
    </div>
  );
}

function ProductRow({ p, editing, onEdit, onCancel, onSave, onRetry, busy }) {
  const [price, setPrice] = useState((p.unit_amount_cents / 100).toFixed(2));
  const [name, setName] = useState(p.name);
  const [enabled, setEnabled] = useState(!!p.enabled);
  useEffect(() => {
    setPrice((p.unit_amount_cents / 100).toFixed(2));
    setName(p.name);
    setEnabled(!!p.enabled);
  }, [p.code, p.unit_amount_cents, p.name, p.enabled]);

  const doSave = () => {
    const cents = Math.round(parseFloat(price) * 100);
    if (!cents || isNaN(cents) || cents <= 0) return toast.error("Preço inválido");
    onSave({ unit_amount_cents: cents, name, enabled });
  };

  if (editing) {
    return (
      <tr className="border-t border-slate-100 bg-amber-50/30" data-testid={`mineral-admin-product-edit-${p.code}`}>
        <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.code}</td>
        <td className="px-4 py-3">
          <input value={name} onChange={e => setName(e.target.value)}
            className="w-full border rounded px-2 py-1 text-sm" data-testid={`mineral-admin-input-name-${p.code}`}/>
        </td>
        <td className="px-4 py-3 text-right">
          <div className="inline-flex items-center gap-1">
            R$ <input type="number" step="0.01" min="0.01"
              value={price} onChange={e => setPrice(e.target.value)}
              data-testid={`mineral-admin-input-price-${p.code}`}
              className="w-20 border rounded px-2 py-1 text-sm text-right"/>
          </div>
        </td>
        <td className="px-4 py-3">
          <label className="inline-flex items-center gap-1 text-xs">
            <input type="checkbox" checked={enabled} onChange={e => setEnabled(e.target.checked)}
                data-testid={`mineral-admin-input-enabled-${p.code}`}/>
            ativo
          </label>
        </td>
        <td className="px-4 py-3 text-xs text-slate-400 font-mono">{p.stripe_product_id || "—"}</td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          <button onClick={doSave} disabled={busy}
            data-testid={`mineral-admin-save-${p.code}`}
            className="inline-flex items-center gap-1 px-3 py-1 rounded-lg text-white text-xs font-medium mr-2"
            style={{ background: MINERAL_COLORS.gold }}>
            <Save className="w-3 h-3"/> Salvar
          </button>
          <button onClick={onCancel} className="text-xs text-slate-500 hover:text-slate-700">Cancelar</button>
        </td>
      </tr>
    );
  }
  return (
    <tr className="border-t border-slate-100 hover:bg-slate-50" data-testid={`mineral-admin-product-${p.code}`}>
      <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.code}</td>
      <td className="px-4 py-3">
        <div className="font-medium" style={{ color: MINERAL_COLORS.navy }}>{p.name}</div>
        {!p.enabled && <span className="text-[10px] text-rose-600 uppercase">inativo</span>}
      </td>
      <td className="px-4 py-3 text-right font-heading font-semibold" style={{ color: MINERAL_COLORS.navy }}>
        R$ {(p.unit_amount_cents / 100).toFixed(2)}
      </td>
      <td className="px-4 py-3"><StatusPill s={p.stripe_sync_status}/></td>
      <td className="px-4 py-3 text-xs text-slate-400 font-mono truncate max-w-[180px]" title={p.stripe_product_id}>
        {p.stripe_product_id || "—"}
      </td>
      <td className="px-4 py-3 text-right whitespace-nowrap">
        {p.stripe_sync_status === "failed" && (
          <button onClick={onRetry} disabled={busy}
            data-testid={`mineral-admin-retry-${p.code}`}
            className="text-xs text-rose-700 hover:text-rose-900 mr-3 inline-flex items-center gap-1">
            <RefreshCw className="w-3 h-3"/> Tentar Novamente
          </button>
        )}
        <button onClick={onEdit}
          data-testid={`mineral-admin-edit-${p.code}`}
          className="text-xs hover:underline"
          style={{ color: MINERAL_COLORS.goldDark }}>
          Editar
        </button>
      </td>
    </tr>
  );
}
